import { AccessibleIcon } from "@offstreetparking/design-system/accessibility";
import { LinkButton } from "@offstreetparking/design-system/forms";
import { Text } from "@offstreetparking/design-system/typography";
import { classNames } from "@offstreetparking/design-system/utils";
import { type ReactNode, useId } from "react";

type IconComponent = (props: { className?: string }) => ReactNode;

type ConfirmationActionRowProps = {
	icon: IconComponent;
	title: string;
	subtitle: string;
	action?: {
		label: string;
		onClick: () => void;
	};
	hasAccent?: boolean;
	iconIntent?: "muted" | "success";
	isLive?: boolean;
};

export const ConfirmationActionRow = ({
	icon: Icon,
	title,
	subtitle,
	action,
	hasAccent = false,
	iconIntent = "muted",
	isLive = false,
}: ConfirmationActionRowProps) => {
	const titleId = useId();
	const actionId = useId();

	return (
		<div
			aria-live={isLive ? "polite" : undefined}
			className={classNames(
				"flex items-center gap-3 border-neutral-faded border-t py-3 pr-4 sm:pr-8",
				hasAccent ? "border-l-4 border-l-primary pl-3 sm:pl-7" : "pl-4 sm:pl-8",
			)}
		>
			<AccessibleIcon>
				<Icon
					className={classNames(
						"size-5 shrink-0",
						iconIntent === "success" ? "text-success" : "text-neutral-faded",
					)}
				/>
			</AccessibleIcon>
			<div className="min-w-0 flex-1">
				<Text id={titleId} weight="bold" className="break-words">
					{title}
				</Text>
				<Text size="sm" intent="muted">
					{subtitle}
				</Text>
			</div>
			{action && (
				<LinkButton
					type="button"
					id={actionId}
					aria-labelledby={`${actionId} ${titleId}`}
					className="shrink-0 font-bold text-accent hover:text-accent-strong"
					onClick={action.onClick}
				>
					{action.label}
				</LinkButton>
			)}
		</div>
	);
};
